import React, { useState, useContext, useEffect } from 'react';
import { t } from 'i18next';
import { Trans } from 'react-i18next';
import {
  Button,
  Paragraph,
  Headings,
  GlobalWarning,
  GlobalSuccess,
  Link,
  ModalOverlayWithoutClose,
  ModalMultiUse,
} from '@sentara-health/optimahealth_web';
import { useForm } from 'react-hook-form';
import { SmsConsent, TextNotificationWrapperProps } from './interface';
import {
  formatePhoneNo,
  errorhandler,
  NumberFormat,
  DeviceTypes,
  useDeviceType,
  ANALYTICS_MESSAGES,
  domainForSegment,
  useAnalytics,
} from 'common';

import {
  SMSTokenResponse,
  usePreferenceUpdate,
  useSMSToken,
} from '@sentara-health/sentara-api-hooks-core';
import { GlobalContext } from '../../context';
import ConfirmIdentityModal from './ConfirmIdentityModal';
import TextNotifications from './TextNotifications';
import { updatePreferenceHelper } from './updatePreferenceHelper';
import { usePreferenceSearchAPI } from 'common/utils/PreferenceSearchAPIUtil';

const TextNotificationWrapper = ({
  smsConsent,
  showButton,
  showPreferences,
  showPrimaryNumberProp,
  cancelConsentProp,
  onSubmitSuccess, // Add this prop
  fromPreferredChannel = false,
  currentTextState = false,
  onConsentChange,
}: TextNotificationWrapperProps) => {
  const {
    register,
    unregister,
    handleSubmit,
    watch,
    setValue,
    getValues,
    setError,
    reset,
    trigger,
    clearErrors,
    formState: { isValid, errors },
  } = useForm({});

  const [showPrimaryNumber, setShowPrimaryNumber] = useState(false);
  const { trackContentListing } = useAnalytics(`${domainForSegment}/profile`);

  const handleSetShowPrimaryNumber = (value: boolean) => {
    if (value) {
      trackContentListing('605', ANALYTICS_MESSAGES.TEXT_COMM_PREF_EDIT, 5);
    }
    setShowPrimaryNumber(value);
  };

  const [smsConsentDetails, setSmsConsentDetails] = useState({} as SmsConsent);
  const { getSMSToken, verifySMSToken } = useSMSToken();
  const [submitClicked, setSubmitClicked] = useState<boolean>(false);
  const [isScrolledToBottom, setIsScrolledToBottom] = useState<boolean>(false);
  const [isConfirmIdentityOpen, setIsConfirmIdentityOpen] =
    useState<boolean>(false);
  const [isConfirmIdentityCancel, setIsConfirmIdentityCancel] =
    useState<boolean>(false);
  const [verificationCode, setVerificationCode] = useState<string>('');
  const [smsValue, setSMSValue] = useState<string>('');
  const [inValidPhoneNumber, setInValidPhoneNumber] = useState<boolean>(false);
  const [inValidOPTError, setInValidOPTError] = useState<boolean>(false);
  const deviceType = useDeviceType();
  const isMobile = deviceType === DeviceTypes.MOBILE;
  const smsInputMaxLength = 6;

  const YesForReceiveTextMsg = (
    <>
      <span>{t('profile.buttonLableOne')}</span>, {t('profile.wantToReceive')}
    </>
  );
  const NoForReceiveTextMsg = (
    <>
      <span>{t('profile.buttonLableTwo')}</span>, {t('profile.notToReceive')}
    </>
  );

  useEffect(() => {
    if (smsConsent) {
      setSmsConsentDetails(smsConsent);
    }
  }, [smsConsent, setValue]);

  useEffect(() => {
    if (smsConsentDetails?.mobileNumber) {
      const phoneNo = smsConsentDetails?.mobileNumber;
      if (
        phoneNo === formatePhoneNo(profileState?.phones?.[0]?.phoneNumber ?? '')
      ) {
        setValue('phoneNoCheckBox', true);
      } else {
        setValue('phoneNoCheckBox', false);
      }
      setValue('phone', smsConsentDetails?.mobileNumber);
    }
  }, [smsConsentDetails, setValue]);

  const handlePhoneNoChange = (event: string) => {
    const phoneNo = formatePhoneNo(event);
    if (
      phoneNo === formatePhoneNo(profileState?.phones?.[0]?.phoneNumber ?? '')
    ) {
      setValue('phoneNoCheckBox', phoneNo);
      setSMSValue(profileState?.phones?.[0]?.phoneNumber ?? '');
    } else {
      setValue('phoneNoCheckBox', false);
      setSMSValue(event);
    }
    setValue('phone', phoneNo);
    getValues('phone');
    setSmsConsentDetails({
      ...smsConsentDetails,
      mobileNumber: phoneNo,
    });
  };

  const clearPhoneNo = () => {
    setValue('phone', '');
    setSmsConsentDetails({
      ...smsConsentDetails,
      mobileNumber: '',
    });
  };

  const cancelConsent = () => {
    trackContentListing('608', ANALYTICS_MESSAGES.TEXT_COMM_PREF_CANCEL, 10);
    reset();
    setShowPrimaryNumber(false);
    if (smsConsent?.termsAndCondition) {
      setValue('termsCondition', smsConsent.termsAndCondition);
      setIsScrolledToBottom(false);
    } else {
      setIsScrolledToBottom(false);
    }
    setSmsConsentDetails(smsConsent as SmsConsent);
  };

  const closePopUpConfirmModal = () => {
    setIsConfirmIdentityOpen(false);
    setIsConfirmIdentityCancel(true);
    setSmsVerified(true);
    cancelConsent();
  };

  const [isCheckedAlert, setIsCheckedAlert] = useState(false);
  const [isTermsModal, setIsTermsModal] = useState(false);
  const [smsVerified, setSmsVerified] = useState(false);

  function cancelPhoneAckAlert() {
    setValue('termsCondition', false);
    setSmsConsentDetails({
      ...smsConsentDetails,
      optInConsent: false,
      termsAndCondition: false,
    });
    setIsCheckedAlert(false);
    setIsTermsModal(false);
    setIsScrolledToBottom(false);
  }

  function confirmPhoneAckAlert() {
    setIsScrolledToBottom(false);
    setValue('termsCondition', false);
    setSmsConsentDetails({
      ...smsConsentDetails,
      optInConsent: true,
      termsAndCondition: false,
    });
    setIsCheckedAlert(false);
  }

  const agreeConstent = () => {
    setValue('termsCondition', true);
    setSmsConsentDetails({
      ...smsConsentDetails,
      optInConsent: true,
      termsAndCondition: true,
    });
    setIsTermsModal(false);
  };

  const [loading, setLoading] = useState<boolean>(false);
  const { updatePreference } = usePreferenceUpdate();
  const { getFeature, loginMemberId, profileState } = useContext(GlobalContext);

  const processPhoneNumber = (smsValue: string) => {
    return smsValue !== ''
      ? smsValue?.replace(NumberFormat, '')
      : smsConsent?.mobileNumber?.replace(NumberFormat, '');
  };

  const handleResponse = async (
    response: SMSTokenResponse,
    smsAPI: boolean = false
  ) => {
    setLoading(false);
    if (response?.errorCode) {
      if (response.errorCode === '404') {
        setInValidOPTError(true);
      } else if (response.errorCode === '400') {
        setInValidPhoneNumber(true);
      } else {
        errorhandler(response.errorCode);
      }
    } else if (smsAPI) {
      setIsConfirmIdentityOpen(true);
    } else {
      updatePreferences();
    }
  };

  const onSubmit = async () => {
    if (smsConsentDetails?.optInConsent) {
      setLoading(true);
      const smsPayload = {
        memberId: btoa(loginMemberId),
        phoneNumber: '1' + processPhoneNumber(smsValue),
      };
      if (getFeature?.SmsShortCode) {
        const response = await getSMSToken(smsPayload);
        handleResponse(response, true);
        setSubmitClicked(false);
        setVerificationCode('');
      } else {
        updatePreferences();
      }
    } else {
      if (
        smsConsent?.mobileNumber == null ||
        (smsConsent?.mobileNumber === '' &&
          smsConsentDetails.mobileNumber === '')
      ) {
        setShowPrimaryNumber(false);
        return;
      }
      updatePreferences();
    }
  };

  const handleOtpSubmit = async () => {
    setLoading(true);
    const otpPayload = {
      memberId: btoa(loginMemberId),
      phoneNumber: '1' + processPhoneNumber(smsValue),
      verificationToken: verificationCode,
    };
    const response = await verifySMSToken(otpPayload);
    handleResponse(response);
  };

  const { fetchProfile } = usePreferenceSearchAPI();

  const calculatePreferenceOption = (): string => {
    if (fromPreferredChannel) {
      if (currentTextState) {
        if (profileState?.preferenceChannel?.toLowerCase().includes('text')) {
          return profileState?.preferenceChannel;
        } else if (
          !profileState?.preferenceChannel ||
          profileState?.preferenceChannel.toLowerCase() === 'none'
        ) {
          // Check if preferenceChannel is 'None' or empty
          return 'text';
        } else {
          return profileState?.preferenceChannel + ',text';
        }
      } else {
        return 'None';
      }
    }

    if (!smsConsentDetails.optInConsent) {
      const updatedPreference = profileState?.preferenceChannel
        ?.split(',')
        .map((item: any) => item.trim())
        .filter((item: any) => item.toLowerCase() !== 'text')
        .join(', ');

      // If updatedPreference is empty or just whitespace, return 'None'
      return updatedPreference && updatedPreference.trim() !== ''
        ? updatedPreference
        : 'None';
    }

    return '';
  };

  const handleSuccessfulUpdate = () => {
    trackContentListing(
      smsConsentDetails.optInConsent ? '606' : '607',
      smsConsentDetails.optInConsent
        ? ANALYTICS_MESSAGES.TEXT_COMM_PREF_OPTIN
        : ANALYTICS_MESSAGES.TEXT_COMM_PREF_OPTOUT,
      smsConsentDetails.optInConsent ? 6 : 7
    );
    fetchProfile({
      onSuccess: () => {
        setShowPrimaryNumber(false);
        setIsConfirmIdentityOpen(false);
        setSubmitClicked(true);

        if (smsConsentDetails.optInConsent) {
          setIsConfirmIdentityCancel(false);
          setSmsVerified(true);
        }

        setValue('phone', profileState?.smsConsent?.mobileNumber);
        if (onSubmitSuccess) {
          onSubmitSuccess();
        }
        setLoading(false);
      },
    });
  };

  const updatePreferences = async () => {
    setLoading(true);

    const preferenceOption = calculatePreferenceOption();

    const apiPayload = updatePreferenceHelper({
      loginMemberId,
      status: smsConsentDetails.optInConsent ? 'Opt-in' : 'Opt-out',
      telecom: [
        {
          system: 'phone',
          value: smsConsentDetails.mobileNumber,
          use: smsConsentDetails.phoneType,
        },
      ],
      termsAndCondition: smsConsentDetails.termsAndCondition,
      ...(fromPreferredChannel || !smsConsentDetails.optInConsent
        ? {
            preferences: {
              option: preferenceOption,
            },
          }
        : {}),
    });

    const response = await updatePreference(apiPayload);

    if (response?.errorCode) {
      errorhandler(response?.errorCode);
      trackContentListing('609', ANALYTICS_MESSAGES.TEXT_COMM_PREF_FAILURE, 11);
      setLoading(false);
    } else {
      handleSuccessfulUpdate();
    }
  };

  const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const { scrollHeight, scrollTop, clientHeight } = e.currentTarget;
    const epsilon = 2;
    const bottom = scrollHeight - scrollTop - clientHeight < epsilon;
    setIsScrolledToBottom(prev => prev || bottom);
  };

  const onPhoneNotificationsAndConsentHandle = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    await trigger(['termsCondition']);
    if (event.target.checked) {
      clearErrors('termsCondition');
      register('termsCondition', { required: false });
    } else {
      register('termsCondition', { required: true });
      setError('termsCondition', { type: 'required' });
    }
  };

  useEffect(() => {
    setSmsConsentDetails({
      optInConsent: fromPreferredChannel
        ? true
        : smsConsent?.optInConsent ?? false,
      mobileNumber: formatePhoneNo(smsConsent?.mobileNumber ?? ''),
      phoneType: smsConsent?.phoneType ?? 'Mobile',
      termsAndCondition: smsConsent?.termsAndCondition ?? false,
    });
    if (smsConsent?.mobileNumber) {
      setValue('phone', smsConsent.mobileNumber);
    }

    if (smsConsent?.termsAndCondition) {
      setValue('termsCondition', true);
    }
  }, [smsConsent, fromPreferredChannel]);

  useEffect(() => {
    if (smsVerified) {
      const timer = setTimeout(() => {
        setSmsVerified(false);
      }, 4000); // 4 seconds

      return () => clearTimeout(timer);
    }
  }, [smsVerified]);

  // Notify parent about consent state changes
  useEffect(() => {
    if (onConsentChange) {
      const hasPhone = !!(
        smsConsentDetails?.mobileNumber &&
        smsConsentDetails.mobileNumber.length >= 14
      );
      onConsentChange(
        smsConsentDetails?.optInConsent || false,
        smsConsentDetails?.termsAndCondition || false,
        hasPhone,
        smsConsentDetails?.mobileNumber || '',
        smsConsentDetails?.phoneType || 'Mobile'
      );
    }
  }, [
    smsConsentDetails?.optInConsent,
    smsConsentDetails?.termsAndCondition,
    smsConsentDetails?.mobileNumber,
    smsConsentDetails?.phoneType,
  ]);

  return (
    <>
      <TextNotifications
        showPreferences={showPreferences}
        isTermsModal={isTermsModal}
        showButton={showButton}
        showPrimaryNumber={showPrimaryNumberProp || showPrimaryNumber}
        smsConsent={smsConsentDetails}
        state={profileState}
        setShowPrimaryNumber={handleSetShowPrimaryNumber}
        handlePhoneNoChange={handlePhoneNoChange}
        isScrolledToBottom={isScrolledToBottom}
        handleScroll={handleScroll}
        clearPhoneNo={clearPhoneNo}
        cancelConsent={
          cancelConsentProp ? () => cancelConsentProp() : cancelConsent
        }
        onSubmit={handleSubmit(onSubmit)}
        smsConsentDetails={smsConsentDetails}
        setSmsConsentDetails={setSmsConsentDetails}
        setIsCheckedAlert={setIsCheckedAlert}
        setIsTermsModal={setIsTermsModal}
        inValidPhoneNumber={inValidPhoneNumber}
        setInValidPhoneNumber={setInValidPhoneNumber}
        YesForReceiveTextMsg={YesForReceiveTextMsg}
        NoForReceiveTextMsg={NoForReceiveTextMsg}
        loading={loading}
        errors={errors}
        watch={watch}
        register={register}
        setValue={setValue}
        clearErrors={clearErrors}
        unregister={unregister}
        isValid={isValid}
        onPhoneNotificationsAndConsentHandle={
          onPhoneNotificationsAndConsentHandle
        }
        handleSubmit={handleSubmit}
        isCheckedAlert={isCheckedAlert}
        cancelPhoneAckAlert={cancelPhoneAckAlert}
        confirmPhoneAckAlert={confirmPhoneAckAlert}
        fromPreferredChannel={fromPreferredChannel}
      />

      {/* Success and Cancel Toast Messages */}
      {smsVerified &&
        (!isConfirmIdentityCancel ? (
          <div className="fade-toast">
            <GlobalSuccess variant="my-3" dataTestId="usernameServiceErr12">
              {t('profile.optedSuccess')}
            </GlobalSuccess>
          </div>
        ) : (
          <div className="fade-toast">
            <GlobalWarning variant="my-3" dataTestId="usernameServiceErr12">
              {t('profile.optedCancel')}
            </GlobalWarning>
          </div>
        ))}

      {/* Terms and Conditions Modal */}
      <ModalOverlayWithoutClose
        isOpen={isTermsModal}
        onClose={() => setIsTermsModal(false)}
        overlayInfo="Modal information"
        className="isTermsModal"
      >
        <div className="modal-header border-bottom-1 pb-4">
          {isMobile ? (
            <Paragraph
              level={3}
              text={t('profile.phoneNotificationTermsModalTitle')}
              className="modal-title"
              variant="medium"
            />
          ) : (
            <Headings
              text={t('profile.phoneNotificationTermsModalTitle')}
              className="modal-title"
              variant="semiBold"
            />
          )}
          <button
            type="button"
            className="modal-close"
            data-dismiss="modal"
            aria-label="Close"
            data-testid="closeModalButton"
            onClick={() => setIsTermsModal(false)}
          />
        </div>
        <div className="modal-body pt-4 pb-5" onScroll={handleScroll}>
          <Paragraph
            text={
              <Trans
                i18nKey="profile.phoneNotificationConsent"
                components={{
                  link1: (
                    <Link
                      to={`tel:${t('profile.toNumber')}`}
                      className="ws-nowrap text-decoration-underline px-1"
                    >
                      {t('profile.toNumber')}
                    </Link>
                  ),
                  link2: (
                    <Link
                      to={`tel:${t('profile.toNumber')}`}
                      className="ws-nowrap text-decoration-underline px-1"
                    >
                      {t('profile.toNumber')}
                    </Link>
                  ),
                }}
              />
            }
            variant="regular"
            data-testid="phoneAcknowledgementTextArea"
            className="mb-0 lineBrake"
          />
        </div>
        <div className="modal-footer border-top-1 gap-3">
          <div className="row">
            <div className="col-sm-auto col-12 mb-sm-0 mb-3">
              <Button
                dataTestId={'imSureButton'}
                variant="primary_btn"
                type="submit"
                className="btn-md-height"
                disabled={!isScrolledToBottom}
                onClick={() => {
                  agreeConstent();
                  onPhoneNotificationsAndConsentHandle({
                    target: { checked: true },
                  } as React.ChangeEvent<HTMLInputElement>);
                }}
              >
                {t('profile.agreeTerms')}
              </Button>
            </div>
            <div className="col-sm-auto col-12">
              <Button
                dataTestId={'imSureButton'}
                variant="secondary_btn"
                type="submit"
                className="btn-md-width btn-md-height"
                onClick={() => {
                  cancelPhoneAckAlert();
                }}
              >
                {t('common.cancel')}
              </Button>
            </div>
          </div>
        </div>
      </ModalOverlayWithoutClose>

      {/* Phone Acknowledgement Modal */}
      <ModalMultiUse
        header={t('profile.phoneAcknowledgementPopUpTitle')}
        body={
          <Paragraph
            text={t('profile.phoneAcknowledgementPopUpContent')}
            variant="regular"
            className="mb-0"
          />
        }
        footer={
          <>
            <div className="col-md-6 col-12 mb-md-0 mb-3 ps-sm-0 pe-sm-2 px-0">
              <Button
                dataTestId={'imSureButton'}
                variant="primary_btn"
                className=" btn-md-height w-100"
                type="button"
                onClick={() => confirmPhoneAckAlert()}
              >
                {t('profile.phoneAcknowledgementPopUpImSure')}
              </Button>
            </div>

            <div className="col-md-6 col-12 ps-sm-2 pe-sm-0 px-0">
              <Button
                dataTestId={'signupButton'}
                variant="secondary_btn"
                className=" btn-md-height w-100 p-0"
                onClick={() => cancelPhoneAckAlert()}
              >
                {t('profile.phoneAcknowledgementPopUpSignup')}
              </Button>
            </div>
          </>
        }
        variant="defaultNoPyBody"
        isMobile={isMobile}
        isOpen={isCheckedAlert}
        onClose={cancelPhoneAckAlert}
        data-testid="phoneAcknowledgementPopUp"
      />

      {/* Confirm Identity Modal */}
      <ConfirmIdentityModal
        closePopUpConfirmModal={closePopUpConfirmModal}
        isConfirmIdentityOpen={isConfirmIdentityOpen}
        getValues={getValues}
        verificationCode={verificationCode}
        setVerificationCode={setVerificationCode}
        submitClicked={submitClicked}
        inValidOPTError={inValidOPTError}
        setInValidOPTError={setInValidOPTError}
        setSubmitClicked={setSubmitClicked}
        onSubmit={onSubmit}
        handleOtpSubmit={handleOtpSubmit}
        t={t}
        smsInputMaxLength={smsInputMaxLength}
      />
    </>
  );
};

export default TextNotificationWrapper;
